import {computed, inject, Injectable} from '@angular/core';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {GetCurrentUserResponse, PermissionId, UserService} from '../../api';
import {lastValueFrom} from 'rxjs';
import {TokenService} from '../auth/token.service';
import {QueryKey} from '../constants/query-key';

@Injectable({
  providedIn: 'root',
})
export class CurrentUserService {
  private readonly userService = inject(UserService);
  private readonly tokenService = inject(TokenService);

  userQuery = injectQuery(() => ({
    queryKey: [QueryKey.CURRENT_USER],
    queryFn: () => lastValueFrom(this.userService.getCurrentUser()),
    enabled: this.tokenService.isAuthenticated(),
  }));

  currentUser = computed<GetCurrentUserResponse | undefined>(() => this.userQuery.data());

  // Country pre-filled from the connected user's locale region: fr-BE/nl-BE -> BE, fr-FR -> FR.
  // Falls back to Belgium for any other (or missing) locale so it always matches a country-dropdown option.
  defaultCountryIsoCode = computed(() => {
    const region = this.currentUser()?.localeIsoCode?.split('-')[1]?.toUpperCase();
    return region === 'FR' ? 'FR' : 'BE';
  });
  // avatarUrl is an authenticated proxy URL (/User/{id}/Avatar?v=...): it requires a Bearer token,
  // so it can't be used directly in an <img> tag — it must go through the bearerImage pipe.
  avatarUrl = computed<string | null>(() => this.currentUser()?.avatarUrl ?? null);
  hasFinanceAccess = computed(() => this.currentUser()?.permissionIds.includes(PermissionId.CanAccessFinances));
  hasPeppolAccess = computed(() => this.currentUser()?.permissionIds.includes(PermissionId.CanAccessPeppol));
  hasManagePermissions = computed(() => this.currentUser()?.permissionIds.includes(PermissionId.CanManagePermissions));
  hasLicenceServerAccess = computed(() => this.currentUser()?.permissionIds.includes(PermissionId.CanManageLicenceServer));
  hasManageProduct = computed(() => this.currentUser()?.permissionIds.includes(PermissionId.CanManageProduct));
  hasValidTimesheet = computed(() => this.currentUser()?.permissionIds.includes(PermissionId.CanValidTimesheet));
  hasManageCustomerProducts = computed(() => this.currentUser()?.permissionIds.includes(PermissionId.CanManageCustomerProducts));
  hasSettingsAccess = computed(() => this.currentUser()?.permissionIds.includes(PermissionId.CanAccessSettings));
  hasDocumentAccess = computed(() => this.currentUser()?.permissionIds.includes(PermissionId.CanAccessDocument));
  hasTimesheetAccess = computed(() => this.currentUser()?.permissionIds.includes(PermissionId.CanAccessTimesheet));
  hasTodoAccess = computed(() => this.currentUser()?.permissionIds.includes(PermissionId.CanAccessTodo));
  hasManageUser = computed(() => this.currentUser()?.permissionIds.includes(PermissionId.CanManageUser));
}
